import { first, filter, map } from 'rxjs/operators';
import {
  ComponentFactoryResolver,
  Injectable,
  Injector,
  ComponentFactory,
  ComponentRef,
  Type,
  ViewContainerRef,
} from '@angular/core';
import {
  castArray,
  forEachRight,
  get,
  intersectionWith,
  isFunction,
} from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import { ActiveModal, ModalRef, ModalCommand, ModalParams } from '..';

import { getValue } from '@app/shared/rx/get-value.helper';
import { ModalComponent } from '@app/modules/modal-components-common/modal.component';

@Injectable()
export class CoreModalService {
  private viewContainerRef: ViewContainerRef | null;
  private childContainerRef: ViewContainerRef | null;

  private readonly openModals$: BehaviorSubject<ModalRef[]>;

  public constructor() {
    this.openModals$ = new BehaviorSubject<ModalRef[]>([]);
  }

  public setContainerRef(viewContainerRef: ViewContainerRef) {
    this.viewContainerRef = viewContainerRef;
  }

  public setChildContainerRef(viewContainerRef: ViewContainerRef) {
    this.childContainerRef = viewContainerRef;
  }

  public removeContainerReference() {
    this.viewContainerRef = null;
  }

  public removeChildContainerReference() {
    this.childContainerRef = null;
  }

  public hasOpenedModals$(): Observable<boolean> {
    return this.openModals$.pipe(
      map((modals: ModalRef[]) => modals.length > 0),
    );
  }

  public hasOpenedModal(): boolean {
    const modals = getValue(this.openModals$);

    return modals?.length > 0;
  }

  public hasOpenedModalOfType<T extends ModalComponent>(
    modalType: Type<T>,
  ): boolean {
    const modals = getValue(this.openModals$);

    return (
      !!modals && modals.some((modalRef) => modalRef.isInstanceOf(modalType))
    );
  }

  public hasOnlyOpenModalOfType<T extends ModalComponent>(
    modalTypes: Type<T>[],
    modalRefs?: ModalRef[],
  ): boolean {
    const modals: ModalRef<any>[] = modalRefs || getValue(this.openModals$);

    return (
      !!modals &&
      modals.every((modalRef) => {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < modalTypes.length; i += 1) {
          if (modalRef.isInstanceOf(modalTypes[i])) {
            return true;
          }
        }
      })
    );
  }

  public hasOnlyOpenModalOfType$(
    modalTypes: Type<ModalComponent>[],
  ): Observable<boolean> {
    return this.openModals$.pipe(
      map(
        (modals: ModalRef[]) =>
          modals.length > 0 && !this.hasOnlyOpenModalOfType(modalTypes, modals),
      ),
    );
  }

  public async waitForNoOpenModalsOfType(
    modalType: Type<ModalComponent>,
  ): Promise<void>;
  public async waitForNoOpenModalsOfType(
    modalTypes: Type<ModalComponent>[],
  ): Promise<void>;
  public async waitForNoOpenModalsOfType(
    modalTypes: Type<ModalComponent> | Type<ModalComponent>[],
  ): Promise<void> {
    const types = castArray(modalTypes);

    await this.openModals$
      .pipe(
        filter((modals: ModalRef[]) => {
          const found = intersectionWith(
            modals,
            types,
            (modal: ModalRef, modalType: Type<ModalComponent>) =>
              modal.isInstanceOf(modalType),
          );
          return !found.length;
        }),
        first(),
      )
      .toPromise();
  }

  public closeModalOfType<T extends ModalComponent>(
    modalType: Type<T>,
    command?: ModalCommand,
  ): void {
    const modals = getValue(this.openModals$).filter((modal) =>
      modal.isInstanceOf(modalType),
    );

    modals[modals.length - 1]?.executeAndClose(command);
  }

  public closeLatestModal(command?: ModalCommand): void {
    const modals = getValue(this.openModals$);
    const modal = modals[modals.length - 1];

    modal?.executeAndClose(command);
  }

  public closeAllModals(command?: ModalCommand): void {
    const modals = this.openModals$.getValue();
    forEachRight(modals, (modal: ModalRef) => {
      // =(
      modal.getModalInstance()?.validateActiveRefund?.();
      modal.executeAndClose(command);
    });
  }

  public getOpenModalOfType<T extends ModalComponent>(
    modalType: Type<T>,
  ): ModalRef | undefined {
    const modals = getValue(this.openModals$);

    return modals?.find((modal) => modal.isInstanceOf(modalType));
  }

  public createComponent<T>(
    component: Type<ModalComponent>,
    injector: Injector,
    params?: ModalParams,
    isChild = false,
  ): ModalRef<T> {
    const activeModal = new ActiveModal();
    const componentRef = this.addModalAndProvidedActiveModal(
      component,
      injector,
      activeModal,
      isChild,
    );
    this.applyParamsToModal(componentRef, params);

    const modal = this.createAndMapModalRef<T>(componentRef, activeModal);
    this.saveModalToOpenModals(modal);
    return modal;
  }

  public getOpenedModalNames(): string[] {
    const modals = this.openModals$.getValue();

    return modals?.map((modal) => modal?.getModalName());
  }

  private addModalAndProvidedActiveModal(
    component: Type<ModalComponent>,
    injector: Injector,
    activeModal: ActiveModal,
    isChild: boolean,
  ): ComponentRef<ModalComponent> {
    const componentFactory = injector
      .get(ComponentFactoryResolver)
      .resolveComponentFactory<ModalComponent>(component);

    const modalContentInjector = Injector.create({
      providers: [
        {
          provide: ActiveModal,
          useValue: activeModal,
        },
      ],
      parent: injector,
    });

    return this.addModalToView(componentFactory, modalContentInjector, isChild);
  }

  private addModalToView(
    componentFactory: ComponentFactory<ModalComponent>,
    injector: Injector,
    isChild: boolean,
  ): ComponentRef<ModalComponent> {
    if (isChild && !this.childContainerRef) {
      throw new Error('Child view container detached');
    }
    if (!this.viewContainerRef) {
      throw new Error('View container detached');
    }

    if (isChild) {
      return this.childContainerRef.createComponent<ModalComponent>(
        componentFactory,
        undefined,
        injector,
      );
    }

    return this.viewContainerRef.createComponent<ModalComponent>(
      componentFactory,
      undefined,
      injector,
    );
  }

  private createAndMapModalRef<T>(
    componentRef: ComponentRef<ModalComponent>,
    activeModal: ActiveModal,
  ): ModalRef<T> {
    const modalRef = new ModalRef<T>(componentRef);
    activeModal.execute = (result: ModalCommand<T>) => {
      modalRef.executeAndClose(result);
    };
    activeModal.dismiss = (reason: any) => {
      modalRef.dismiss(reason);
    };
    componentRef.onDestroy(() => this.removeModalFromOpenModals(modalRef));
    return modalRef;
  }

  private saveModalToOpenModals(modal: ModalRef) {
    const modals = this.openModals$.getValue();
    modals.push(modal);
    this.openModals$.next(modals);
  }

  private removeModalFromOpenModals(modal: ModalRef): void {
    const modals = this.openModals$.getValue();
    const index = modals.indexOf(modal);
    if (index > -1) {
      modals.splice(index, 1);
    }
    this.openModals$.next(modals);
  }

  private applyParamsToModal(
    componentRef: ComponentRef<ModalComponent>,
    params?: ModalParams,
  ): void {
    if (params) {
      Object.assign(componentRef.instance, params.data);
    }
    if (isFunction(get(componentRef, 'changeDetectorRef.detectChanges'))) {
      componentRef.changeDetectorRef.detectChanges();
    }
  }
}
